import React from 'react'
import { Sheet, Spinner, useTheme } from 'tamagui'
import CloseIcon from '@mui/icons-material/Close'
import { Virtuoso } from 'react-virtuoso'
import { CobuHeader, CobuSimpleAvatar, CobuStack, CobuText } from '@cobu/ui'
import type { PlaceReactionPerson } from '@cobu/types'
import { Strings } from '../../constants/Strings'

interface PeopleListSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  people: PlaceReactionPerson[]
  loading?: boolean
}

/**
 * Bottom sheet listing the neighbors who liked / have "been" to a place, opened
 * by tapping the like/been count. Names come from the backend pre-formatted
 * "First L." (no unit numbers), so they're rendered as-is.
 */
export function PeopleListSheet({ open, onOpenChange, title, people, loading }: PeopleListSheetProps) {
  const theme = useTheme()

  return (
    <Sheet
      modal
      open={open}
      onOpenChange={onOpenChange}
      snapPoints={[70, 40]}
      snapPointsMode="percent"
      dismissOnSnapToBottom
    >
      <Sheet.Overlay bg={theme.blackTransparent50?.val} />
      <Sheet.Frame br="$3" style={{ width: '100%', padding: 0, backgroundColor: '#fff' }}>
        <CobuStack f={1} bg="white" style={{ maxHeight: '80vh' }}>
          <CobuHeader
            title={title}
            renderLeft={() => <CobuStack w="$10" />}
            renderRight={() => (
              <CobuStack onPress={() => onOpenChange(false)} p="$2">
                <CloseIcon style={{ fontSize: 20, color: theme.colorMuted?.val }} />
              </CobuStack>
            )}
          />

          {loading ? (
            <CobuStack f={1} ai="center" jc="center" p="$4">
              <Spinner color="$colorMuted" />
            </CobuStack>
          ) : people.length === 0 ? (
            <CobuStack f={1} ai="center" jc="center" p="$4">
              <CobuText variant="body" color="$colorMuted">{Strings.get().places.people.empty}</CobuText>
            </CobuStack>
          ) : (
            <Virtuoso
              data={people}
              computeItemKey={(_index, person) => person.uuid}
              itemContent={(_index, person) => (
                <CobuStack layout="horizontal" ai="center" gap="$3" p="$4">
                  <CobuSimpleAvatar user={{ uuid: person.uuid, displayName: person.name }} />
                  <CobuText variant="body" fontWeight="600">
                    {person.name}
                  </CobuText>
                </CobuStack>
              )}
              style={{ height: '60vh', width: '100%' }}
            />
          )}
        </CobuStack>
      </Sheet.Frame>
    </Sheet>
  )
}
